import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesFileMessages } from "@/i18n/messages/frames-file";
import { cn } from "@/lib/cn";
import {
  useRevealLabel,
  type FileActions,
  type FileNotice,
} from "./use-file-actions";

/** What a file frame says instead of its file, by notice. */
export function FileNoticeText({
  notice,
  className,
}: {
  notice: FileNotice;
  className?: string;
}) {
  const t = useT(framesFileMessages);
  const text =
    notice.kind === "elsewhere"
      ? t("fileOnOtherComputer", {
          name: notice.computer || t("anotherComputer"),
        })
      : notice.kind === "missing"
        ? t("fileNotFoundHint")
        : notice.kind === "unlinked"
          ? t("notLinkedHint")
          : t("folderAccessDenied");
  const heading =
    notice.kind === "missing"
      ? t("fileNotFound")
      : notice.kind === "unlinked"
        ? t("notLinkedHere")
        : null;
  return (
    <p
      role="status"
      className={cn("text-card-muted", className)}
      data-testid="file-frame-notice"
      data-notice={notice.kind}
    >
      {heading && (
        <span className="block font-medium text-card-ink">{heading}</span>
      )}
      {text}
    </p>
  );
}

/**
 * A file's buttons where it is shown: Open, Show in Finder or Explorer,
 * Locate…, Link a file… and Allow in Settings, each only where it applies.
 */
export function FileActionButtons({
  actions,
  name,
  scale,
  className,
}: {
  actions: FileActions;
  /** The file's name, for Open's label. */
  name: string;
  /**
   * `object`: sized in em, with the file card it sits under; `cover`: on a
   * PDF's cover, in the app's sizes.
   */
  scale: "object" | "cover";
  className?: string;
}) {
  const t = useT(framesFileMessages);
  const tc = useT(commonMessages);
  const revealLabel = useRevealLabel();
  const { onOpen, onReveal, onLocate, onLinkFile, onAllow } = actions;
  if (!onOpen && !onReveal && !onLocate && !onLinkFile && !onAllow) {
    return null;
  }
  const object = scale === "object";
  const button = object
    ? "nodrag h-[2.2em] gap-[0.4em] px-[1em] text-[0.62em]"
    : "nodrag";
  const icon = object ? "h-[1.1em] w-[1.1em]" : "h-3.5 w-3.5";
  return (
    <div className={cn("flex flex-wrap gap-[0.5em]", className)}>
      {onLocate && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={button}
          onClick={onLocate}
          data-testid="file-frame-locate"
        >
          <Icons.Search className={icon} aria-hidden="true" />
          {t("locateFile")}
        </Button>
      )}
      {onLinkFile && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={button}
          onClick={onLinkFile}
          data-testid="file-frame-link-file"
        >
          <Icons.Search className={icon} aria-hidden="true" />
          {t("linkFile")}
        </Button>
      )}
      {onAllow && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={button}
          onClick={onAllow}
          data-testid="file-frame-allow"
        >
          <Icons.Settings className={icon} aria-hidden="true" />
          {t("allowInSettings")}
        </Button>
      )}
      {onOpen && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={button}
          aria-label={t("openFile", { name: name || t("thisFile") })}
          onClick={onOpen}
          data-testid="file-frame-open"
        >
          <Icons.ExternalLink className={icon} aria-hidden="true" />
          {tc("open")}
        </Button>
      )}
      {onReveal && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={button}
          onClick={onReveal}
          data-testid="file-frame-reveal"
        >
          <Icons.FolderOpen className={icon} aria-hidden="true" />
          {revealLabel}
        </Button>
      )}
    </div>
  );
}
